/**
 * The match screen: plays the match (useMatchFrames), provides its per-tick
 * frame to the layers and panels, and composes the input (selection, pointer
 * orders, placement and targeting modes, hotkeys) and command feedback with
 * the 3D view (MatchScene) and everything over it (GameHud).
 */
import { useCallback, useRef, useState } from 'react';
import type { MatchSettings } from '@/game/sim';
import { GameHud } from './game/app/GameHud';
import { MatchScene } from './game/app/MatchScene';
import { FrameProvider } from './game/app/simFrame';
import { useBoxSelect } from './game/app/useBoxSelect';
import { useCameraControl } from './game/app/useCameraControl';
import { useCommandFeedback } from './game/app/useCommandFeedback';
import { useHotkeys } from './game/app/useHotkeys';
import { useMatchFrames } from './game/app/useMatchFrames';
import { useMatchModes } from './game/app/useMatchModes';
import { usePointerOrders } from './game/app/usePointerOrders';
import { useSelection } from './game/app/useSelection';
import { useSendTroops } from './game/app/useSendTroops';
import { Marquee } from './game/Marquee';
import { usePerfCommit } from './game/PerfOverlay';
import { useQuality } from './game/QualitySettings';
import { usePersistedNames } from './game/ui/persistNames';
import { usePersistedSlots } from './game/ui/persistSlots';
import { createUiStore } from './game/ui/store';
import { WorkHint } from './game/WorkHint';

export function GameVisuals3D({
  players,
  settings,
  names,
}: {
  players?: string[];
  /** Players' names by id, for display (lib/names.ts). */
  names?: ReadonlyMap<string, string>;
  /** The admin's match settings (the same on every peer); defaults without. */
  settings?: MatchSettings;
} = {}) {
  // `?perf=1`: this render through the HUD's commit (PerfOverlay).
  usePerfCommit();
  // Input state (hover, selection, placement and targeting modes, the
  // player's own names): a store, so handlers read it without ref mirrors and
  // hovering re-renders only what shows the hovered tile (ui/store.ts).
  const [ui] = useState(createUiStore);
  // Stable: the frame store is rebuilt when it changes.
  const entityNames = useCallback(() => ui.get().names, [ui]);
  const {
    frames,
    mapRadius,
    dispatch: send,
    isHost,
  } = useMatchFrames(players, settings, names, entityNames);
  // This screen re-renders with every sim change (the runner's version), so
  // reading the frame here keeps its per-tick effects in step with it.
  const frame = frames.get();

  usePersistedNames(ui, frame.real.match?.seed ?? '', frame.playerId, frame.tick);
  usePersistedSlots(ui, frame.real.match?.seed ?? '', frame.playerId, frame.tick);
  // Graphics quality (High / Low / Auto), persisted.
  const quality = useQuality();

  const { dispatch, toasts, pushToasts, cancelled, spends } = useCommandFeedback(
    frames,
    frame,
    send
  );
  const camera = useCameraControl(frames);
  const selection = useSelection(ui, frames, frame, camera.panTo);
  const modes = useMatchModes(ui, frames, frame);
  const sendTroops = useSendTroops(ui, frames, frame, dispatch, selection);
  const areaRef = useRef<HTMLDivElement>(null);
  const { setInspectKey: clearInspect } = selection;
  const onGroundSelect = useCallback(() => clearInspect(null), [clearInspect]);
  const boxSelect = useBoxSelect({
    ui,
    frames,
    areaRef,
    cameraRef: camera.viewCameraRef,
    selectOnMap: selection.selectOnMap,
    onGroundSelect,
  });
  const pointer = usePointerOrders({
    ui,
    frames,
    modes,
    dispatch,
    pushToasts,
    setInspectKey: selection.setInspectKey,
    onGroundDown: boxSelect.onGroundDown,
  });
  const { setInspectKey } = selection;
  const { close: closeSendTroops } = sendTroops;
  useHotkeys({
    ui,
    frames,
    dispatch,
    onEscape: () => {
      closeSendTroops();
      setInspectKey(null);
    },
    onDigit: selection.tapDigit,
    onAssignSlot: selection.assignSlot,
    onIdleCycle: selection.cycleIdle,
    onAttackMove: () => modes.enterTargeting({ kind: 'attackMove' }),
  });

  // Cities chip row: a click selects the city without moving the camera;
  // while placing, placement moves to that city's territory (its panel is the
  // one placement returns to).
  const { placeFromCity } = modes;
  const { selectBuilding, panToCity } = selection;
  const selectCity = useCallback(
    (id: string) => {
      if (!frames.get().view.buildings[id]) return;
      placeFromCity(id);
      selectBuilding(id);
    },
    [frames, placeFromCity, selectBuilding]
  );
  const showCity = useCallback(
    (id: string) => {
      selectCity(id);
      panToCity(id);
    },
    [selectCity, panToCity]
  );

  return (
    <FrameProvider store={frames}>
      <div
        ref={areaRef}
        role="application"
        className="h-full w-full bg-black"
        onContextMenu={(e) => e.preventDefault()}
      >
        <MatchScene
          ui={ui}
          quality={quality}
          mapRadius={mapRadius}
          modes={modes}
          selection={selection}
          pointer={pointer}
          attackRings={sendTroops.rings}
          setCamera={camera.setCamera}
          viewCameraRef={camera.viewCameraRef}
          onSelectUnit={boxSelect.onSelectUnit}
          drawnRef={boxSelect.drawnRef}
        />
        <Marquee ui={ui} />
        <WorkHint ui={ui} />
        <GameHud
          ui={ui}
          dispatch={dispatch}
          toasts={toasts}
          spends={spends}
          cancelled={cancelled}
          modes={modes}
          selection={selection}
          sendTroops={sendTroops}
          quality={quality}
          mapRadius={mapRadius}
          isHost={isHost}
          viewCameraRef={camera.viewCameraRef}
          panTo={camera.panTo}
          panToWorld={camera.panToWorld}
          onSelectCity={selectCity}
          onShowCity={showCity}
        />
      </div>
    </FrameProvider>
  );
}
